import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ScreenSizeService } from '../../../../services/utils/screenSize.service';
import { Review } from '../../../models/review.model';
import { ReviewService } from '../../../../services/review.service';
import { ReviewDetailsComponent } from '../review-details/review-details.component';
import { ModalOffcanvasService } from '../../../../services/modal-offcanvas.service';
import { OffcanvasHistoryService } from '../../../../services/offcanvas-history.service';
import { OpenWriteReviewModalService } from '../../../../services/openWriteReviewModal.service';
import { TranslateService } from '@ngx-translate/core';
import { ViewStatus } from '@uranda/ui';

@Component({
    selector: 'app-review-sidebar',
    templateUrl: './review-sidebar.component.html',
    styleUrls: ['./review-sidebar.component.scss'],
    standalone: false
})
export class ReviewSidebarComponent implements OnInit {
  @Input() partnerId?: number;
  @Input() clinicName?: string;
  @Input() averageRating?: number;
  @Input() totalReviews?: number;
  reviewStats?: Review;
  viewStatus = ViewStatus.LOADING;
  @ViewChildren('lastReview') reviewElements!: QueryList<ElementRef>;
  private observer?: IntersectionObserver;

  hasMoreReviews = true;
  currentPage = 0;
  pageSize = 10;
  displayedReviews: Review[] = [];
  loadingReviews = true;
  hasReviews = false;
  
  constructor(
    private offCanvasHistory: OffcanvasHistoryService,
    private screenSize: ScreenSizeService,
    private reviewService: ReviewService,
    private translateService: TranslateService,
    private modalOffcanvasService: ModalOffcanvasService,
    private uselessService: OpenWriteReviewModalService
  ) { }

  async ngOnInit() {
    // Load review stats first
    await this.loadReviewStats();
    // Then load reviews
    await this.loadMoreReviews();
  }

  ngAfterViewInit() {
    this.reviewElements.changes.subscribe(() => {
      this.observeLastReview();
    });
  }

  async loadReviewStats() {
    if (!this.partnerId) return;
    
    try {
      this.viewStatus = ViewStatus.LOADING;
      // Assuming your ReviewService has a method to get stats
      // Adjust this based on your actual API method
      this.reviewStats = await this.reviewService.getReviewStats(this.partnerId.toString());
      this.viewStatus = ViewStatus.READY;
    } catch (error) {
      console.error('Error loading review stats:', error);
      this.viewStatus = ViewStatus.ERROR;
    }
  }

  observeLastReview() {
    if (this.observer) this.observer.disconnect();
    const lastReviewEl = this.reviewElements.last?.nativeElement;

    if (lastReviewEl) {
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.hasMoreReviews) {
            this.loadMoreReviews();
          }
        });
      });
      this.observer.observe(lastReviewEl);
    }
  }

  closeOffcanvas(result?: string) {
    this.offCanvasHistory.dismiss(result);
  }

  openWriteReviewModal(result?: string) {
    if (this.screenSize.isDesktop) {
      this.offCanvasHistory.dismiss();
      setTimeout(() => {
        document.body.style.overflow = 'hidden';
      }, 500);
    }
    this.uselessService.openWriteReviewModal({ id: this.partnerId!, name: this.clinicName! }, true).then(() => {
      setTimeout(() => {
        if (this.screenSize.isDesktop == false) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = 'auto';
        }
      }, 500);
    }).catch((error) => {
      console.error('Error opening write review modal:', error);
    });
  }

  async loadMoreReviews() {
    if (!this.hasMoreReviews || !this.partnerId) return;

    try {
      const response = await this.reviewService.getReviewsByPartnerId(
        this.partnerId.toString(),
        true,
        undefined,
        this.currentPage,
        this.pageSize
      );

      if (response.totalElements > 0) {
        this.hasReviews = true;
      }

      if (this.currentPage === 0) {
        this.totalReviews = response.totalElements;
      }

      const newReviews = response.content;
      this.displayedReviews = [...this.displayedReviews, ...newReviews];

      this.currentPage++;
      if (newReviews.length < this.pageSize || response.last) {
        this.hasMoreReviews = false;
        if (this.observer) this.observer.disconnect();
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      this.loadingReviews = false;
    }
  }

  trackByReviewId(index: number, review: any): any {
    return review.id || index;
  }

  async reviewDetails(review: Review) {
    await this.closeOffcanvas();

    this.modalOffcanvasService.openComponent(ReviewDetailsComponent, {
      review: review,
      isOffcanvas: this.screenSize.isMobile
    });
  }

  get reviewsLabel(): string {
    const count = this.totalReviews ?? 0;
    const review = count === 1 ? 'review' : 'reviews';
    return `${count} ${this.translateService.instant(review)}`;
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}